<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { useRouter } from 'vue-router'
import type { BountyDto } from '@roadfootage/api-client'
import { useCreatorStore } from '@/stores/creator'
import { useBountyResponseStore } from '@/stores/bounty-response'
import { useIncidentSubmissionStore } from '@/stores/incident-submission'
import { useFileUploadStore } from '@/stores/file-upload'
import { formatDateTime } from '@/utils/format'

const props = defineProps<{ modelValue: boolean; bounty: BountyDto }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const $q = useQuasar()
const router = useRouter()
const creatorStore = useCreatorStore()
const responseStore = useBountyResponseStore()
const incidentStore = useIncidentSubmissionStore()
const fileUploadStore = useFileUploadStore()
const { incidentReportList, loading } = storeToRefs(creatorStore)
const { isSubmitting } = storeToRefs(responseStore)

const selectedReportId = ref<string | null>(null)
const errorMessage = ref<string | null>(null)

const options = computed(() =>
  incidentReportList.value.map((report) => ({
    value: report.id as string,
    label: [
      report.address?.formattedAddress || 'Location processing...',
      report.incidentDateTime ? formatDateTime(report.incidentDateTime) : null
    ]
      .filter(Boolean)
      .join(' - ')
  }))
)

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return
    // A fetch from an earlier open is still running: a reset now would let two fetches add duplicate options.
    if (loading.value) return
    selectedReportId.value = null
    errorMessage.value = null
    creatorStore.$reset()
    await creatorStore.fetchCreatorIncidentReports()
  },
  { immediate: true }
)

const close = () => emit('update:modelValue', false)

async function sendResponse() {
  if (!selectedReportId.value) return
  errorMessage.value = null
  responseStore.startResponse(props.bounty)
  const result = await responseStore.submitResponse(selectedReportId.value)
  responseStore.cancelResponse()
  if (!result.ok) {
    errorMessage.value = result.message
    return
  }
  $q.notify({ type: 'positive', message: 'Response sent to the bounty poster.', position: 'top' })
  close()
}

async function uploadNewFootage() {
  incidentStore.$reset()
  fileUploadStore.$reset()
  responseStore.startResponse(props.bounty)
  incidentStore.prefillFromBounty(props.bounty)
  close()
  await router.push({ name: 'upload' })
}
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)">
    <q-card style="min-width: 300px; max-width: 480px; width: 100%">
      <q-card-section class="row items-center no-wrap q-pb-none">
        <div class="text-h6 col">Respond with footage</div>
        <q-btn flat round dense icon="mdi-close" aria-label="Close" @click="close" />
      </q-card-section>

      <q-card-section>
        <div class="text-subtitle2 q-mb-xs">Use one of my reports</div>
        <div v-if="loading && !options.length" class="flex flex-center q-pa-md">
          <q-spinner />
        </div>
        <div v-else-if="!options.length" class="text-grey-7">You have no reports yet.</div>
        <q-option-group
          v-else
          v-model="selectedReportId"
          type="radio"
          :options="options"
          :disable="isSubmitting"
        />
        <q-banner v-if="errorMessage" dense rounded class="bg-red-1 text-red-9 q-mt-sm">
          {{ errorMessage }}
        </q-banner>
      </q-card-section>

      <q-card-actions vertical align="stretch" class="q-px-md q-pb-md q-gutter-y-sm">
        <q-btn
          unelevated
          color="blue-8"
          label="Send response"
          :disable="!selectedReportId"
          :loading="isSubmitting"
          @click="sendResponse"
        />
        <q-separator />
        <q-btn
          flat
          color="blue-8"
          icon="mdi-cloud-upload"
          label="Upload new footage"
          :disable="isSubmitting"
          @click="uploadNewFootage"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
